import moment from 'moment';
import {
  PROVIDER_LABELS,
  SAUCE_CHANNEL_NAME,
  providerNamesMatch,
  normalizeProviderName,
  getProviderDisplayName
} from 'helpers/constants/storeSettings';

const formatList = (items) => {
  if (!items || items.length === 0) return '';
  if (items.length === 1) return items[0];
  return `${items.slice(0, -1).join(', ')}, ${items[items.length - 1]}`;
};

const formatListWithAnd = (items) => {
  if (!items || items.length === 0) return '';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

export function buildDisableWarning(external) {
  const codes = Array.isArray(external?.ProvidersList)
    ? external.ProvidersList
    : [];
  const names = codes.map((c) => PROVIDER_LABELS[c] || c).filter(Boolean);

  if (names.length > 0) {
    const providers = formatList(names);
    return `Pay attention that disabling your store will fully disable ${providers} and your Sauce Direct stores and prevent the users from making orders.`;
  }

  return 'Disabling your store means no new orders can be placed until it’s turned back on';
}

export function buildDisableWarningFromPlatformNames(names) {
  const filtered = (names || []).filter(Boolean);

  if (filtered.length > 0) {
    const providers = formatList(filtered);
    return `Pay attention that disabling your store will fully disable ${providers} and your Sauce Direct stores and prevent the users from making orders.`;
  }

  return 'Disabling your store means no new orders can be placed until it\u2019s turned back on';
}

export function buildAvailabilityWarning(names) {
  const filtered = (names || []).filter(Boolean);

  if (filtered.length > 0) {
    const channels = formatListWithAnd([...filtered, 'Direct']);
    return `Any changes to store availability will affect new orders across all your connected ordering channels - ${channels}`;
  }

  return 'Any changes to store availability will affect new direct orders';
}

export const CHANNEL_SELECTION_AVAILABILITY_WARNING =
  'Pausing a channel stops new orders from that provider. Other channels keep running.';

export const getPauseSettings = (externalOrderProviders) =>
  Array.isArray(externalOrderProviders?.PauseSettings)
    ? externalOrderProviders.PauseSettings
    : [];

export const isPauseSettingActive = (setting) => {
  // Backend keeps only currently paused providers in PauseSettings.
  // Do not rely on PausedUntil for status — it can be timezone-skewed.
  return !!setting?.Name;
};

export const findActivePauseSetting = (pauseSettings, providerName) =>
  (pauseSettings || []).find((setting) =>
    providerNamesMatch(setting?.Name, providerName)
  );

export const isProviderPaused = (pauseSettings, providerName) =>
  !!findActivePauseSetting(pauseSettings, providerName);

export const getExternalPlatformNames = (externalPlatforms = []) =>
  (externalPlatforms || []).map((p) => p.platformName || p).filter(Boolean);

export const areAllChannelsPaused = ({
  closedManually,
  externalPlatforms,
  pauseSettings
}) => {
  const externalNames = getExternalPlatformNames(externalPlatforms);
  const allExternalsPaused =
    externalNames.length === 0 ||
    externalNames.every((name) => isProviderPaused(pauseSettings, name));

  return !!closedManually && allExternalsPaused;
};

export const upsertPauseSetting = (pauseSettings, nextSetting) => {
  const list = Array.isArray(pauseSettings) ? [...pauseSettings] : [];
  const index = list.findIndex((setting) =>
    providerNamesMatch(setting?.Name, nextSetting.Name)
  );

  if (index >= 0) {
    list[index] = {...list[index], ...nextSetting};
  } else {
    list.push(nextSetting);
  }

  return list;
};

export const removePauseSetting = (pauseSettings, providerName) =>
  (Array.isArray(pauseSettings) ? pauseSettings : []).filter(
    (setting) => !providerNamesMatch(setting?.Name, providerName)
  );

export const buildExternalOrderProvidersPayload = (
  externalOrderProviders,
  pauseSettings
) => ({
  ...(externalOrderProviders || {}),
  PauseSettings: pauseSettings
});

const uniqueChannelNames = (names) => {
  const seen = new Set();
  const channels = [];

  names.forEach((name) => {
    if (typeof name !== 'string') return;
    const trimmed = name.trim();
    const key = normalizeProviderName(trimmed);
    if (!key || seen.has(key)) return;
    seen.add(key);
    channels.push(trimmed);
  });

  return channels;
};

const formatPauseUntil = (until) => {
  if (until == null || until === '') return undefined;
  if (until instanceof Date) return until.toISOString();
  return until;
};

const orderChannelNames = (names) =>
  uniqueChannelNames(names).filter(
    (name) => !providerNamesMatch(name, SAUCE_CHANNEL_NAME)
  );

export const buildPauseLocationBody = ({
  closeRestaurant = false,
  closedManuallyUntilTime,
  closedManuallyReason,
  storeDisableOption,
  until,
  channelNames = []
} = {}) => {
  const body = {};
  const channels = orderChannelNames(channelNames);
  const restaurantUntil = formatPauseUntil(closedManuallyUntilTime);
  const channelUntil = formatPauseUntil(until);

  if (closeRestaurant) {
    body.ClosedManually = true;
    if (restaurantUntil != null) body.ClosedManuallyUntilTime = restaurantUntil;
    if (storeDisableOption != null)
      body.StoreDisableOption = storeDisableOption;
  }

  if (closedManuallyReason != null) {
    body.ClosedManuallyReason = closedManuallyReason;
  }

  if (channels.length) {
    body.channels = channels;
    if (channelUntil != null) body.until = channelUntil;
  }

  return body;
};

export const buildUnpauseLocationBody = ({
  openRestaurant = false,
  channelNames = []
} = {}) => {
  const body = {};
  const channels = orderChannelNames(channelNames);

  if (openRestaurant) body.ClosedManually = false;
  if (channels.length) body.channels = channels;

  return body;
};

export const getChannelsStatusText = ({
  closedManually,
  externalPlatforms,
  pauseSettings
}) => {
  if (
    areAllChannelsPaused({closedManually, externalPlatforms, pauseSettings})
  ) {
    return 'All channels are paused and not accepting orders.';
  }

  const externalNames = getExternalPlatformNames(externalPlatforms);
  const totalChannels = externalNames.length + 1;
  const pausedExternalCount = externalNames.filter((name) =>
    isProviderPaused(pauseSettings, name)
  ).length;
  const pausedCount = pausedExternalCount + (closedManually ? 1 : 0);

  if (pausedCount === 0) {
    return 'All channels are active and accepting orders.';
  }

  return `${pausedCount} of ${totalChannels} channels are paused.`;
};

export const formatPausedUntil = (pausedUntil) => {
  if (!pausedUntil) return '';
  const calcTime =
    typeof pausedUntil === 'object'
      ? Number(Date.parse(pausedUntil)) / 1000
      : pausedUntil;

  if (typeof calcTime === 'number' || /^\d+$/.test(String(calcTime))) {
    const asNumber = Number(calcTime);
    // unix seconds vs ms
    const unix = asNumber > 1e12 ? Math.floor(asNumber / 1000) : asNumber;
    return moment.unix(unix).utc().format('MM/DD/YYYY HH:mm');
  }

  return moment(pausedUntil).utc().format('MM/DD/YYYY HH:mm');
};

export const getPausedChannelsForBanners = ({
  closedManually,
  closedManuallyUntilTime,
  externalOrderProviders,
  platformNames = []
}) => {
  const pauseSettings = getPauseSettings(externalOrderProviders);
  const channels = [];

  if (closedManually) {
    channels.push({
      Name: SAUCE_CHANNEL_NAME,
      displayName: getProviderDisplayName(SAUCE_CHANNEL_NAME),
      PausedUntil: closedManuallyUntilTime || null,
      isSauce: true
    });
  }

  pauseSettings
    .filter(isPauseSettingActive)
    .filter((setting) => !providerNamesMatch(setting.Name, SAUCE_CHANNEL_NAME))
    .forEach((setting) => {
      const known =
        !platformNames.length ||
        platformNames.some((name) => providerNamesMatch(name, setting.Name));

      if (!known) return;

      channels.push({
        ...setting,
        displayName: getProviderDisplayName(setting.Name),
        isSauce: false
      });
    });

  return channels;
};

export const buildPausedChannelsBannerText = (channels) => {
  if (!channels?.length) return '';

  const formatChannel = (channel) => {
    const until = channel.PausedUntil
      ? formatPausedUntil(channel.PausedUntil)
      : '';
    return until
      ? `${channel.displayName} until ${until}`
      : channel.displayName;
  };

  if (channels.length === 1) {
    const channel = channels[0];
    const until = channel.PausedUntil
      ? formatPausedUntil(channel.PausedUntil)
      : '';
    return until
      ? `${channel.displayName} is paused until ${until}`
      : `${channel.displayName} is paused`;
  }

  const parts = channels.map(formatChannel);
  if (parts.length === 2) {
    return `${parts[0]} and ${parts[1]} are paused`;
  }

  return `${parts.slice(0, -1).join(', ')}, and ${
    parts[parts.length - 1]
  } are paused`;
};

export const getPausedUntilKey = (pausedUntil) => {
  if (!pausedUntil && pausedUntil !== 0) return 'no-until';
  return formatPausedUntil(pausedUntil) || 'no-until';
};

export const formatPausedChannelNames = (channels) => {
  const names = (channels || []).map((channel) => channel.displayName);
  if (!names.length) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

export const buildPausedGroupBannerText = (channels) => {
  if (!channels?.length) return '';

  const names = formatPausedChannelNames(channels);
  const until = channels[0]?.PausedUntil
    ? formatPausedUntil(channels[0].PausedUntil)
    : '';
  const verb = channels.length === 1 ? 'is' : 'are';

  return until
    ? `${names} ${verb} paused until ${until}`
    : `${names} ${verb} paused`;
};

export const groupPausedChannelsByUntil = (channels = []) => {
  const groupsMap = new Map();

  channels.forEach((channel) => {
    const key = getPausedUntilKey(channel.PausedUntil);
    if (!groupsMap.has(key)) {
      groupsMap.set(key, []);
    }
    groupsMap.get(key).push(channel);
  });

  return Array.from(groupsMap.entries()).map(([untilKey, groupChannels]) => ({
    untilKey,
    untilLabel: untilKey === 'no-until' ? '' : untilKey,
    channels: groupChannels
  }));
};

export default {
  buildDisableWarning,
  buildDisableWarningFromPlatformNames,
  buildAvailabilityWarning,
  CHANNEL_SELECTION_AVAILABILITY_WARNING,
  getPauseSettings,
  isPauseSettingActive,
  findActivePauseSetting,
  isProviderPaused,
  getExternalPlatformNames,
  areAllChannelsPaused,
  upsertPauseSetting,
  removePauseSetting,
  buildExternalOrderProvidersPayload,
  getChannelsStatusText,
  formatPausedUntil,
  getPausedChannelsForBanners,
  buildPausedChannelsBannerText,
  getPausedUntilKey,
  formatPausedChannelNames,
  buildPausedGroupBannerText,
  groupPausedChannelsByUntil
};
